import { Injectable } from "@angular/core";
import { Content, ContentStack, ContentText, TableCell, TDocumentDefinitions } from "pdfmake/interfaces";
import { Utils } from "../../core/utils";
import { NoteComment } from "../../notes/shared/note.model";
import { PDFExportService } from "../../shared/pdf-export/pdf-export.service";
import { NotesTableDataRow } from "../notes-table/notes-table.model";
import { NoteExportData } from "./note-export.model";

@Injectable({
	providedIn: 'root'
})
export class NoteExportService {
	constructor(private pdfExportService: PDFExportService) {}

	exportToPDF(exportData: NoteExportData) {
		const pdfBody: TableCell[][] = [exportData.columns.map(c =>
			({ text: c.header, style: 'headerRow' } as TableCell))
		];

		const docDefinition: TDocumentDefinitions = {
			content: [
				{
					text: exportData.headerMessage,
					alignment: 'center',
					bold: true,
					fontSize: 16,
					margin: [0, 0, 0, 10]
				},
				{
					text: 'Query Parameters',
					alignment: 'center',
					bold: true
				},
				{
					columns: [
						{ width: '*', text: '' },
						{
							width: 'auto',
							margin: [0, 5, 0, 15],
							table: {
								body: Object.entries(exportData.queryParams).map(kvp => {
									const [key, value] = kvp;

									return [{ text: key, alignment: 'right', italics: true }, value] as TableCell[];
								})
							}
						},
						{ width: '*', text: '' }
					]
				},
				{
					alignment: 'center',
					table: {
						headerRows: 1,
						widths: Array(exportData.columns.length).fill('auto'),
						body: pdfBody
					},
					layout: {
						hLineWidth: (i, node) => {
							const bodyRow = node.table.body[i];

							return bodyRow && (bodyRow[exportData.noteColIndex] as any).colSpan ? 0 : 1;
						},
						vLineWidth: (i, node) => [0, node.table.widths!.length].includes(i) ? 1 : 0,
						hLineColor: (i, node) => '#e9ecef',
						vLineColor: (i, node) => '#e9ecef'
					}
				}
			],
			pageOrientation: exportData.pdfExportOptions.orientation,
			images: {} // populated dynamically
		};

		this.pdfExportService.setDocDefinition(docDefinition, exportData.pdfExportOptions.printerFriendly);

		this.buildExport(exportData, pdfBody);

		this.pdfExportService.exportPDF();
	}

	private buildExport(exportData: NoteExportData, pdfBody: TableCell[][]) {
		for (const note of exportData.notes) {
			const noteCells: TableCell[] = [];
			pdfBody.push(noteCells);

			for (const col of exportData.columns) {
				switch (col.field) {
					case 'timeframe':
						this.buildTimeframeCells(note, noteCells);
						break;
					case 'turnover':
						noteCells.push({
							text: note.turnover ? '🗹' : '□',
							font: 'Unicode',
							margin: [0, 3, 0, 0]
						});
						break;
					case 'createdBy':
						noteCells.push(note.createdBy.firstName + ' ' + note.createdBy.lastName);
						break;
					case 'priority':
						noteCells.push(note.maxPriority);
						break;
					case 'tags':
						noteCells.push(note.tagsDisplayString || '');
						break;
					case 'note':
						this.buildNoteCells(note, noteCells, exportData, pdfBody);
						break;
					case 'acknowledgments':
						const acks = note.acknowledgements.map(ack =>
							`${Utils.formatDateString(ack.updated)} by ${ack.createdBy.firstName} ${ack.createdBy.lastName}`
						).join('\n\n');

						noteCells.push(acks);
						break;
					case 'updated':
						noteCells.push(Utils.formatDateString(note.updated));
						break;
					case 'updatedBy':
						noteCells.push(note.updatedBy.firstName + ' ' + note.updatedBy.lastName);
						break;
				}
			}

			if (note.noteComments.length && exportData.pdfExportOptions.showComments) {
				const comments = this.getExportNoteCommentRow(exportData, note.noteComments);
				pdfBody.push(comments);
			}
		}
	}

	private buildTimeframeCells(note: NotesTableDataRow, noteCells: TableCell[]) {
		const text: Content = [{
			text: Utils.formatDateString(note.noteTime),
			style: 'linkColor',
			link: `${window.location.origin}/#/note-detail?note=${note._links.self.href}&log=${note.logId}`
		}];

		const borderDetails: any = { borderColor: undefined };

		if (note.carryForward) {
			text.push({
				text: '  🕘',
				font: 'Unicode'
			});

			text.push({ text: '\r\nCarry Forward\r\n' });

			let carryForwardEnded = false;

			if (note.carryForwardEndTime) {
				const ended = new Date(note.carryForwardEndTime!) < new Date(),
					message = `\n${ended ? 'Ended' : 'To'} ${Utils.formatDateString(note.carryForwardEndTime!)}`;
				
				carryForwardEnded = ended;
				text.push(message);
			}
			else
				text.push('No End Time Set');

			borderDetails.borderColor = [carryForwardEnded ? 'blue' : 'red'];
		}

		noteCells.push({ text: text, ...borderDetails });
	}

	private buildNoteCells(note: NotesTableDataRow, noteCells: TableCell[], exportData: NoteExportData, pdfBody: TableCell[][]) {
		const noteHtml = document.createElement('div');
		noteHtml.innerHTML = note.safeHtmlNote;

		// Excludes from PDF export images known to cause exceptions, including images pasted while using the Safari browser 
		// (before we were able to handle these with quill-image-drop-and-paste) and GIF images (unsupported by pdfmake)
		noteHtml.querySelectorAll('img').forEach(img => {
			if (img.src.endsWith(':0/') || // pasted from Safari before this was supported
				note.attachments.find(a => a.contentType == 'image/gif' && a.id.toString() == img.src.split('/').pop()))
				img.remove();
		});

		const inspectionNote = noteHtml.lastElementChild?.className == 'table-container';

		if (inspectionNote) {
			const templateHeading = this.pdfExportService.getNoteTemplateHeading(noteHtml.firstElementChild!);
			noteCells.push(templateHeading);

			const cells = exportData.getCellArray();
			cells[exportData.noteColIndex] = {
				...this.pdfExportService.getInspectionNoteCells(noteHtml.lastElementChild!.children),
				colSpan: exportData.columns.length - exportData.noteColIndex
			};

			pdfBody.push(cells);
		}
		else {
			// Rich Text toggled on
			let hasRichText = !!note.richText,
				oorMessages: Element | null = null;

			if (!hasRichText) {
				oorMessages = noteHtml.querySelector('[data-oor-msg]');
				oorMessages && noteHtml.removeChild(oorMessages);

				// Note has markdown-rendered HTML (build as rich text)
				hasRichText = !!noteHtml.querySelector(':not(p):not(br)');
			}

			if (hasRichText)
				this.buildRichTextNoteCells(noteHtml, exportData, note, noteCells, pdfBody);
			else
				this.buildBasicOrOperationalNoteCells(noteHtml, oorMessages, noteCells);
		}
	}

	private buildRichTextNoteCells(noteHtml: HTMLDivElement, exportData: NoteExportData, note: NotesTableDataRow, noteCells: TableCell[], pdfBody: TableCell[][]) {
		const elements = Array.from(noteHtml.childNodes)
			.filter(node => node.nodeType == 1 || (node.nodeType == 3 && (node.textContent ?? '').trim().length > 0))
			.map(node => {
				if (node.nodeType == 1)
					return node;

				// Text content nodes are transferred into new p elements
				const newElement = document.createElement('p');
				newElement.innerHTML = node.textContent ?? '';

				return newElement;
			}) as Element[];

		const richTextColumn = this.pdfExportService.getRichTextCells(elements);

		// Adds some spacing for markdown-rendered elements
		!note.richText && richTextColumn.stack.forEach(ct => {
			if (typeof ct != 'string')
				(ct as ContentText).margin = [0, 10, 0, 0];
		});

		if (exportData.noteIsLastColumn)
			noteCells.push(richTextColumn);
		else {
			(richTextColumn as any).colSpan = exportData.columns.length - exportData.noteColIndex;

			const richTextCells = exportData.getCellArray();
			richTextCells[exportData.noteColIndex] = richTextColumn;

			noteCells.push('');
			pdfBody.push(richTextCells);
		}
	}

	private buildBasicOrOperationalNoteCells(noteHtml: HTMLDivElement, oorMessages: Element | null, noteCells: TableCell[]) {
		// Some multiline operational notes have <br> tags in the safeHtmlNote field (not sure why)
		noteHtml.innerHTML = noteHtml.innerHTML.replace(/<br>/g, '\n');

		const noteText = Array.from(noteHtml.children).map(p => p.textContent).join('\n\n'), contentStack: ContentStack = {
			alignment: 'left',
			stack: [{
				text: this.pdfExportService.getWrappableCellText(noteText)
			}]
		};

		// Operational note with one or more out-of-range messages
		if (oorMessages) {
			(contentStack.stack[0] as ContentText).margin = [0, 0, 0, 10];

			for (const node of Array.from(oorMessages.childNodes)) {
				node.nodeName == '#text' && contentStack.stack.push({
					text: node.textContent!,
					style: 'alarmColor'
				});
			}
		}

		noteCells.push(contentStack);
	}

	private getExportNoteCommentRow(exportData: NoteExportData, noteComments: NoteComment[]) {
		const commentCells = exportData.getCellArray(),
			commentStack: ContentStack = {
				margin: [0, 10, 0, 0],
				stack: [{
					text: [
						{ text: '💬   ', font: 'Unicode' },
						{ text: 'Comments', italics: true, decoration: 'underline' }, ':'
					]
				}]
			};
	
		noteComments.forEach(nc => 
			commentStack.stack.push({
				margin: [0, 12, 0, 0],
				italics: true,
				font: 'Courier',
				text: `${Utils.formatDateString(nc.updated)} - ${nc.updatedBy.firstName} ${nc.updatedBy.lastName}\n`
			}, nc.comment)
		);

		commentCells[exportData.noteColIndex] = {
			...commentStack,
			colSpan: exportData.columns.length - exportData.noteColIndex,
			alignment: 'left'
		};

		return commentCells;
	}
}